<template>
  <main
    class="min-h-screen bg-slate-800 px-4 py-12 text-white md:px-6 md:py-16"
  >
    <section class="mx-auto max-w-6xl">
      <p
        class="text-sm font-semibold uppercase tracking-[0.2em] text-rtcblue-300 md:tracking-[0.25em]"
      >
        {{ $t("contact.eyebrow") }}
      </p>

      <h1
        class="mt-3 max-w-4xl text-4xl font-extrabold tracking-tight md:text-5xl"
      >
        {{ $t("contact.title") }}
      </h1>

      <p class="mt-4 max-w-2xl text-base leading-8 text-slate-300 md:text-lg">
        {{ $t("contact.intro") }}
      </p>

      <!-- CONTACT DETAILS -->

      <div class="mt-8 grid gap-6 md:mt-10 md:grid-cols-2">
        <div class="rounded-3xl border border-white/10 bg-white/10 p-6 md:p-8">
          <h2 class="text-xl font-bold">{{ $t("contact.contactHeading") }}</h2>

          <ul class="list-disc contact-list">
            <li>
              <a
                href="mailto:info@rtc4water.com"
                class="mt-3 block break-words text-lg text-rtcblue-300 transition hover:text-rtcblue-200"
              >
                info@rtc4water.com
              </a>
            </li>
            <li>
              <a
                href="https://www.linkedin.com/company/rtc4water-luxembourg/"
                target="_blank"
                rel="noopener noreferrer"
                class="mt-3 block text-lg text-rtcblue-300 transition hover:text-rtcblue-200"
              >
                {{ $t("contact.linkedin") }}
              </a>
            </li>
          </ul>
        </div>

        <div class="rounded-3xl border border-white/10 bg-white/10 p-6 md:p-8">
          <h2 class="text-xl font-bold">{{ $t("contact.officeHeading") }}</h2>

          <div class="mt-3 space-y-2 text-slate-300">
            <p class="font-semibold text-white">RTC4Water™</p>

            <p>
              {{ $t("contact.tel") }}
              <a
                href="tel:+35224836976"
                class="text-rtcblue-300 hover:text-rtcblue-200"
              >
                +352 24 83 69 76
              </a>
            </p>

            <a
              href="https://www.google.com/maps/dir/?api=1&amp;destination=RTC4Water%2C%2062A%20Grand%20Rue%2C%20L-3394%20Roeser%2C%20Luxembourg"
              target="_blank"
              rel="noopener noreferrer"
              class="mt-2 flex items-start gap-2 pt-2 leading-7 text-slate-300 transition hover:text-rtcblue-200"
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                class="mt-1 h-5 w-5 shrink-0 text-rtcblue-300"
              >
                <path
                  d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z"
                />
              </svg>
              <span>
                62A, Grand Rue<br />
                L-3394, Roeser<br />
                Luxembourg
              </span>
            </a>

            <a
              href="https://www.google.com/maps/dir/?api=1&amp;destination=RTC4Water%2C%2062A%20Grand%20Rue%2C%20L-3394%20Roeser%2C%20Luxembourg"
              target="_blank"
              rel="noopener noreferrer"
              class="mt-3 inline-flex items-center gap-2 rounded-full bg-rtcblue-500 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-rtcblue-400"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" class="h-4 w-4">
                <path
                  d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z"
                />
              </svg>
              {{ $t("contact.route") }}
            </a>
          </div>
        </div>
      </div>

      <!-- LOCATION + PARKING -->

      <div class="mt-6 grid gap-8 md:mt-6 lg:grid-cols-2">
        <div
          class="overflow-hidden rounded-3xl border border-white/10 bg-white/10 shadow-2xl"
        >
          <img
            src="/assets/parking-rtc4water.png"
            alt="Map showing RTC4Water office location and secondary parking"
            class="w-full object-contain"
          />
        </div>

        <div class="rounded-3xl border border-white/10 bg-white/10 p-6 md:p-8">
          <h2 class="text-2xl font-bold">{{ $t("contact.parkingHeading") }}</h2>

          <div
            class="mt-6 space-y-5 text-base leading-8 text-slate-300 md:text-lg"
          >
            <p>
              {{ $t("contact.parkingBefore")
              }}<span class="text-red-400">{{
                $t("contact.parkingCircle")
              }}</span
              >{{ $t("contact.parkingAfter") }}
            </p>
          </div>
        </div>
      </div>

      <!-- VISITOR INFORMATION -->

      <div
        class="mt-4 grid gap-8 rounded-3xl border border-white/10 bg-white/10 p-6 sm:grid-cols-2 md:mt-6 lg:grid-cols-5 lg:p-8"
      >
        <div
          v-for="(v, i) in $t('contact.visitors')"
          :key="i"
          class="flex items-center gap-4"
        >
          <div
            class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-rtcblue-500/15 text-rtcblue-300"
          >
            <svg
              v-if="i === 0"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 21s-7-6.5-7-11a7 7 0 1 1 14 0c0 4.5-7 11-7 11z" />
              <circle cx="12" cy="10" r="2.5" />
            </svg>
            <svg
              v-else-if="i === 1"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 11l1.6-4A2 2 0 0 1 8.5 6h7a2 2 0 0 1 1.9 1.3L19 11" />
              <rect x="3" y="11" width="18" height="6" rx="2" />
              <circle cx="7.5" cy="17.5" r="1.5" />
              <circle cx="16.5" cy="17.5" r="1.5" />
            </svg>
            <svg
              v-else-if="i === 2"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="5" y="4" width="14" height="12" rx="2" />
              <path d="M5 11h14" />
              <circle cx="8" cy="18.5" r="1.4" />
              <circle cx="16" cy="18.5" r="1.4" />
            </svg>
            <svg
              v-else-if="i === 3"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
            <svg
              v-else
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="4" y="5" width="16" height="16" rx="2" />
              <path d="M4 10h16M8 3v4M16 3v4" />
            </svg>
          </div>
          <div>
            <h3 class="font-bold text-white">{{ v.title }}</h3>
            <p
              class="mt-2 whitespace-pre-line text-sm leading-7 text-slate-300"
            >
              {{ v.text }}
            </p>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
export default {
  name: "ContactView",

  data() {
    return {
      visitorIcons: [
        "/assets/icons/icon-location2.png",
        "/assets/icons/icon-car2.png",
        "/assets/icons/icon-train2.png",
        "/assets/icons/icon-clock.png",
        "/assets/icons/icon-calendar.png",
      ],
    };
  },
};
</script>

<style>
.contact-list {
  margin-left: 50px;
  margin-top: 30px;
}
</style>